// src/components/Footer.js
import React from 'react';
// Import the Linking module from React Native
import { View, Text, Pressable, StyleSheet, Linking, Platform } from 'react-native';

// Icon imports - using @expo/vector-icons (built into Expo)
import { Ionicons } from '@expo/vector-icons';
import { useCookieConsent } from '../context/CookieConsentContext';

// This component now accepts the 'navigation' prop directly
export default function Footer({ navigation }) {
  const cookieConsent = useCookieConsent();
  // --- Function to handle opening the Privacy Policy URL ---
  const openURL = (url) => {
    Linking.canOpenURL(url).then(supported => {
      if (supported) {
        Linking.openURL(url);
      } else {
        console.log("Don't know how to open URI: " + url);
      }
    });
  };

  // Helper: render a link as a Pressable wrapping Text. React Native Web's
  // <Text onPress> is unreliable (no cursor:pointer, click events sometimes
  // swallowed); Pressable fires consistently and produces the right affordance.
  const Link = ({ onPress, style, children }) => (
    <Pressable onPress={onPress} style={({ hovered }) => [hovered && styles.linkHovered]}>
      <Text style={style}>{children}</Text>
    </Pressable>
  );

  return (
    <View style={styles.footer}>
      <Text style={styles.copyrightText}>
       CritCash {new Date().getFullYear()}. All rights reserved.
      </Text>

      <View style={styles.linksContainer}>
        <Link onPress={() => navigation.navigate('About')} style={styles.link}>About Us</Link>
        <Text style={styles.separator}>|</Text>
        <Link onPress={() => navigation.navigate('Legal')} style={styles.link}>Legal</Link>
        <Text style={styles.separator}>|</Text>
        <Link onPress={() => navigation.navigate('HowToPlay')} style={styles.link}>How to Play</Link>
        <Text style={styles.separator}>|</Text>
        <Link onPress={() => navigation.navigate('FAQ')} style={styles.link}>FAQ</Link>
        <Text style={styles.separator}>|</Text>
        <Link onPress={() => navigation.navigate('ResponsibleGaming')} style={styles.link}>Responsible Play</Link>
        {/* External Web Links */}
        <Text style={styles.separator}>|</Text>
        <Link onPress={() => openURL('https://critcash.com/terms-of-service')} style={styles.link}>Terms of Service</Link>
        <Text style={styles.separator}>|</Text>
        <Link onPress={() => openURL('https://critcash.com/privacy-policy')} style={styles.link}>Privacy Policy</Link>
        {Platform.OS === 'web' && cookieConsent?.resetConsent && (
          <>
            <Text style={styles.separator}>|</Text>
            <Link onPress={() => cookieConsent.resetConsent()} style={styles.link}>Cookie Preferences</Link>
          </>
        )}
      </View>

      {/* Partner Links Row */}
      <View style={styles.partnerLinksContainer}>
        <Link onPress={() => navigation.navigate('DeveloperPortal')} style={styles.partnerLink}>
          <Ionicons name="rocket" size={12} color="#999" /> Developers
        </Link>
        <Text style={styles.separator}>|</Text>
        <Link onPress={() => navigation.navigate('Advertiser')} style={styles.partnerLink}>
          <Ionicons name="trending-up" size={12} color="#999" /> Advertisers
        </Link>
        <Text style={styles.separator}>|</Text>
        <Link onPress={() => navigation.navigate('Jobs')} style={styles.partnerLink}>
          <Ionicons name="briefcase" size={12} color="#999" /> Careers
        </Link>
      </View>

      {/* Social Links Row */}
      <View style={styles.socialLinksContainer}>
        <Link onPress={() => openURL('https://x.com/CritCash')} style={styles.partnerLink}>
          <Ionicons name="logo-twitter" size={12} color="#999" /> Twitter
        </Link>
        <Text style={styles.separator}>|</Text>
        <Link onPress={() => openURL('https://www.linkedin.com/company/critcash-inc')} style={styles.partnerLink}>
          <Ionicons name="logo-linkedin" size={12} color="#999" /> LinkedIn
        </Link>
        <Text style={styles.separator}>|</Text>
        {/* Support link — visible on every screen so a stuck user always has
            a way out. Goes to the Help & Support page (self-serve answers +
            ticket form); the page itself carries the mailto fallback. */}
        <Link onPress={() => navigation.navigate('HelpSupport')} style={[styles.partnerLink, { color: '#FCD000' }]}>
          <Ionicons name="help-buoy-outline" size={12} color="#FCD000" /> Help / Support
        </Link>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  footer: {
    paddingVertical: 20,
    paddingHorizontal: 10,
    alignItems: 'center',
    marginTop: 'auto', // Pushes footer to the bottom if content is short
    width: '100%',
    overflow: 'hidden',
  },
  copyrightText: {
    color: '#888',
    fontSize: 12,
    textAlign: 'center',
    marginBottom: 8,
  },
  linksContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap', // Allows links to wrap on smaller screens
    justifyContent: 'center', // Center the links
    alignItems: 'center',
    maxWidth: '100%',
  },
  link: {
    color: '#FCD000', // Using your app's theme color
    fontSize: 12,
    textDecorationLine: 'underline',
  },
  linkHovered: {
    opacity: 0.7,
    cursor: 'pointer',
  },
  separator: {
    color: '#888',
    marginHorizontal: 8,
    fontSize: 12,
  },
  partnerLinksContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#222',
    maxWidth: '100%',
  },
  partnerLink: {
    color: '#999',
    fontSize: 12,
  },
  socialLinksContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 8,
    maxWidth: '100%',
  },
});